/**
 * 單據影像辨識的前端 API(REQ-20260907003)。
 *
 * 目前只有一支:把一張托運單照片送去辨識託運單號。
 *
 * ⚠️ **這張圖不上傳、不落地。** 它只是為了讓後端轉給影像模型讀一次,回來就丟掉;
 * 要留存單據仍走既有的單據附件 / 憑證(`features/document-attachments/api.ts`)。
 * 所以這裡沒有 presign、沒有 MinIO、沒有 react-query 快取 —— 沒有東西可以快取。
 *
 * 走 `multipart/form-data` 而不是 JSON + base64:後端 fastify 的 JSON body limit
 * 是 1 MB(見 api 的 `main.ts`),手機照片轉 base64 一定爆成 413。
 * 寫法照既有的 `settings/MdbImportPage.tsx`。
 */
import {
  TRACKING_NO_SCAN_MAX_BYTES,
  isAllowedTrackingNoScanMime,
  type ApiSuccess,
  type TrackingNoScanResult,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

/** 縮圖後的長邊上限。1600px 對單據上的字來說綽綽有餘,再大只是讓請求變慢變貴 */
const MAX_EDGE = 1600;

/** JPEG 品質。0.8 在單據這種高對比的圖上看不出差別,體積卻掉一個量級 */
const JPEG_QUALITY = 0.8;

export async function scanTrackingNo(file: File): Promise<TrackingNoScanResult> {
  const prepared = await downscaleForScan(file);
  // 縮圖成功時型別一定是 image/jpeg;走 fallback(原檔直送)時才可能是別的。
  // 這一關擺在縮圖**之後**是刻意的:iOS 的 HEIC 有時 `file.type` 是空字串,
  // 但畫得進 canvas —— 先縮再檢查,那些檔就能用了。
  if (!isAllowedTrackingNoScanMime(prepared.type)) {
    throw new Error(`只能辨識圖片檔(收到 ${prepared.type || '未知類型'})`);
  }
  if (prepared.size > TRACKING_NO_SCAN_MAX_BYTES) {
    throw new Error(`圖片過大,上限 ${TRACKING_NO_SCAN_MAX_BYTES / 1024 / 1024} MB`);
  }

  const form = new FormData();
  form.append('file', prepared, prepared.name);
  const res = await apiClient.post<ApiSuccess<TrackingNoScanResult>>(
    '/document-scan/tracking-no',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 },
  );
  return res.data.data;
}

/**
 * 在瀏覽器把照片縮到長邊 ≤ 1600px 的 JPEG。
 *
 * **任何一步失敗都回原檔**(HEIC 畫不出來、拿不到 canvas context、toBlob 回 null、
 * 這個環境根本沒有 canvas)—— 縮圖是為了省流量與時間,不是功能本身。
 * 讓縮圖失敗變成掃描失敗,等於為了省錢把功能關掉。
 */
async function downscaleForScan(file: File): Promise<File> {
  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
    });
    if (!blob) return file;
    return new File([blob], replaceExt(file.name), { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('圖片解不開'));
    };
    img.src = url;
  });
}

function replaceExt(name: string): string {
  const base = name.replace(/\.[^.]+$/, '');
  return `${base || 'scan'}.jpg`;
}
